import React, { useEffect, useRef, useState } from "react";

export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Keyboard (WASD/Arrows) + external joystick vector, normalised to [-1,1].
export const useInput = () => {
  const keys = useRef({});
  const joy = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const dn = (e) => { keys.current[e.key.toLowerCase()] = true; if ([" ", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(e.key.toLowerCase())) e.preventDefault(); };
    const up = (e) => { keys.current[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", dn); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
  }, []);
  const dir = () => {
    const k = keys.current;
    let x = (k["d"] || k["arrowright"] ? 1 : 0) - (k["a"] || k["arrowleft"] ? 1 : 0);
    let y = (k["s"] || k["arrowdown"] ? 1 : 0) - (k["w"] || k["arrowup"] ? 1 : 0);
    x += joy.current.x; y += joy.current.y;
    const m = Math.hypot(x, y) || 1;
    return { x: clamp(x, -1, 1) / (m > 1 ? m : 1), y: clamp(y, -1, 1) / (m > 1 ? m : 1) };
  };
  return { dir, keys, setJoy: (x, y) => { joy.current = { x, y }; } };
};

// requestAnimationFrame loop, delta in seconds, paused when !active.
export const useLoop = (active, cb) => {
  const ref = useRef(cb); ref.current = cb;
  useEffect(() => {
    if (!active) return undefined;
    let raf, last = performance.now();
    const tick = (t) => { const dt = Math.min(0.05, (t - last) / 1000); last = t; ref.current(dt); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
};

// On-screen thumb joystick for touch (and mouse drag).
export const Joystick = ({ onMove }) => {
  const base = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const active = useRef(false);
  const handle = (clientX, clientY) => {
    const r = base.current.getBoundingClientRect();
    let dx = clientX - (r.left + r.width / 2);
    let dy = clientY - (r.top + r.height / 2);
    const max = r.width / 2;
    const m = Math.hypot(dx, dy) || 1;
    if (m > max) { dx = (dx / m) * max; dy = (dy / m) * max; }
    setKnob({ x: dx, y: dy });
    onMove(dx / max, dy / max);
  };
  const end = () => { active.current = false; setKnob({ x: 0, y: 0 }); onMove(0, 0); };
  return (
    <div
      ref={base}
      data-testid="joystick"
      className="relative h-24 w-24 shrink-0 touch-none rounded-full border-2 border-kirana-brown/60 bg-black/15 sm:hidden"
      onTouchStart={(e) => { active.current = true; handle(e.touches[0].clientX, e.touches[0].clientY); }}
      onTouchMove={(e) => { if (active.current) handle(e.touches[0].clientX, e.touches[0].clientY); }}
      onTouchEnd={end}
      onMouseDown={(e) => { active.current = true; handle(e.clientX, e.clientY); }}
      onMouseMove={(e) => { if (active.current) handle(e.clientX, e.clientY); }}
      onMouseUp={end}
      onMouseLeave={end}
    >
      <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-kirana-brown/70" style={{ transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))` }} />
    </div>
  );
};

// A round character token positioned in % of the arena.
export const Token = ({ x, y, color = "#E63946", label, ring, dim, size = 30, z = 5, testid }) => (
  <div
    data-testid={testid}
    className="pointer-events-none absolute flex items-center justify-center rounded-full border-2 border-[#3a2617] transition-[filter] duration-100"
    style={{ left: `${x}%`, top: `${y}%`, width: size, height: size, background: color, transform: "translate(-50%,-50%)", zIndex: z, opacity: dim ? 0.55 : 1, boxShadow: ring ? "0 0 0 3px rgba(255,209,102,0.9)" : "0 2px 3px rgba(0,0,0,0.3)" }}
  >
    {label && <span className="text-[13px] leading-none">{label}</span>}
  </div>
);
